import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TAG_TOOLTIP_DATA } from '@simlab/simlab-facility-management/common';
import { IScanControllerToken } from '@simOn/common/scan';
import { UserInfoCard } from '@simOn/common/user-info-card-models';
import { EventMarkersService, EventsApiService } from '@simOn/event/element/data-access';
import {
  EventDto,
  eventRecurrenceLabel,
  isRecurringEvent,
  normalizeRecurrenceType,
  timeRangeLabel
} from '@simOn/event/element/models';
import { SimIconComponent } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { EventAvatarStackComponent } from '../event-avatar-stack/event-avatar-stack.component';
import { RecurrenceBadgeComponent } from '../recurrence-badge/recurrence-badge.component';

type EventState = { loaded: boolean; event: EventDto | null };

/**
 * Hover card for an event marker on the 3D scan. Rendered by `simlab-tag-tooltip-host`
 * from `ComponentConfiguration.tooltip = { component: EventTooltipComponent }`; the event id
 * comes from the marker `userData` (`eventId`) via `TAG_TOOLTIP_DATA`.
 * Uses the markers cache first and falls back to the event endpoint.
 */
@Component({
  selector: 'event-tooltip',
  imports: [DatePipe, SimIconComponent, SimSpinnerComponent, RecurrenceBadgeComponent, EventAvatarStackComponent],
  templateUrl: './event-tooltip.component.html',
  styleUrl: './event-tooltip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventTooltipComponent {
  private readonly _data = inject(TAG_TOOLTIP_DATA);
  private readonly _markers = inject(EventMarkersService);
  private readonly _api = inject(EventsApiService);
  private readonly _scanController = inject(IScanControllerToken, { optional: true });

  protected readonly eventId = (this._data.userData?.['eventId'] as string | undefined) ?? this._data.id;
  protected readonly canShowMore = !!this._scanController;

  private readonly _state: Signal<EventState> = toSignal(
    this._markers.events$.pipe(
      take(1),
      map((events) => events.find((event) => event.id === this.eventId) ?? null),
      switchMap((cached) => (cached ? of(cached) : this._api.getEvent(this.eventId))),
      map((event): EventState => ({ loaded: true, event })),
      catchError(() => of<EventState>({ loaded: true, event: null }))
    ),
    { initialValue: { loaded: false, event: null } }
  );
  protected readonly event = computed(() => this._state().event);
  protected readonly ready = computed(() => this._state().loaded);

  protected readonly description = computed(() => this.event()?.description?.trim() ?? '');
  protected readonly startDate = computed(() => this.event()?.startDate ?? null);
  /** Second date shown only for non-recurring events spanning more than one day. */
  protected readonly endDate = computed(() => {
    const event = this.event();
    if (!event?.endDate || !event.startDate || this.isRecurring()) return null;
    return sameDay(event.startDate, event.endDate) ? null : event.endDate;
  });
  protected readonly timeRange = computed(() => (this.event() ? timeRangeLabel(this.event()!) : ''));
  protected readonly isRecurring = computed(() =>
    this.event() ? isRecurringEvent(normalizeRecurrenceType(this.event()!.recurrenceType)) : false
  );
  protected readonly recurrenceLabel = computed(() => (this.event() ? eventRecurrenceLabel(this.event()!) : ''));
  protected readonly assignees = computed<UserInfoCard[]>(() => this.event()?.stakeholders ?? []);

  /** Opens the event side sheet through the regular marker click flow. */
  showMore(): void {
    if (!this._scanController) return;
    this._scanController.openMarker({
      id: this._data.id,
      userData: this._data.userData ?? { type: 'events', eventId: this.eventId }
    });
  }
}

function sameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}
